import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import { Box } from '@hivebrite/design-system/Box';
import { IconArrowDown, IconArrowUp } from '@hivebrite/design-system/Icon';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { Skeleton } from '@hivebrite/design-system/Skeleton';
import { primaryShadow, zIndices } from '@hivebrite/design-system/Theme';
import { useOnClickOutside } from '@hivebrite/react-hooks';

import { prepareItems } from 'hb-react/frontoffice/apps/Menu/utils';
import getNextFocusableElement from 'hb-react/shared/utils/getNextFocusableElement';
import keyCodes from 'helpers/keyCodes';

import Item from '../MobileItem';

const SubItems = (items, onClick) =>
  items.map((subItem) => (
    <Box
      key={subItem.title}
      as="li"
      role="none"
      flexDirection="column"
      width="100%"
    >
      <MobileItem {...subItem} onClick={onClick}>
        {subItem.title}
      </MobileItem>
    </Box>
  ));

const MobileMenuComponent = ({
  getPageUrl,
  dynamic,
  items,
  rootPath,
  ariaLabel,
  menuId,
  labelledById,
}) => {
  const wrapperRef = useRef();
  const buttonRef = useRef();
  const menuRef = useRef();
  const { pathname } = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(null);

  const preparedItems = useMemo(
    () => (dynamic ? prepareItems(items, getPageUrl) : items),
    [dynamic, items, getPageUrl],
  );

  const updateCurrentTab = useCallback(() => {
    const newCurrenTab = () => {
      let currentTab;

      preparedItems.forEach((item) => {
        if (dynamic && item.subItems.length !== 0 && item.url !== pathname) {
          item.subItems.forEach((subItem) => {
            if (subItem.url === pathname) {
              currentTab = subItem;
            }
          });
        } else if (dynamic && item.url === pathname) {
          currentTab = item;
        }
      });

      return currentTab;
    };

    setCurrentTab(newCurrenTab);
  }, [pathname, preparedItems, dynamic]);

  const closeMenu = () => {
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  const handleClickOutside = () => setIsOpen(false);

  const handleDropdownKeyDown = (e) => {
    if (e.keyCode === keyCodes.enter || e.keyCode === keyCodes.space) {
      e.preventDefault();

      setIsOpen(true);
      setTimeout(() => {
        getNextFocusableElement(menuRef.current, 1)?.focus();
      }, 0);
    }
  };

  const handleItemPress = () => {
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  useEffect(() => {
    updateCurrentTab();
  }, [updateCurrentTab]);

  useEffect(() => {
    const handleKeyboardNavigation = (e) => {
      if (e.keyCode === keyCodes.escape) {
        closeMenu();
      }

      if (e.keyCode === keyCodes.arrowDown || e.keyCode === keyCodes.arrowUp) {
        const step = e.keyCode === keyCodes.arrowDown ? 1 : -1;
        const nextElement = getNextFocusableElement(menuRef.current, step);

        e.preventDefault();
        nextElement?.focus();
      }
    };

    isOpen &&
      window.addEventListener('keydown', handleKeyboardNavigation, true);

    return () => {
      window.removeEventListener('keydown', handleKeyboardNavigation, true);
    };
  }, [isOpen]);

  useOnClickOutside(wrapperRef, handleClickOutside);

  return (
    <Box
      width="100%"
      flexDirection="column"
      position="relative"
      ref={wrapperRef}
    >
      <ScreenReadersContent id={labelledById}>{ariaLabel}</ScreenReadersContent>
      <Dropdown
        ref={buttonRef}
        onKeyDown={handleDropdownKeyDown}
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        width="100%"
        height={40}
        justifyContent="space-between"
        pl={4}
        pr={3}
        aria-labelledby={labelledById}
        aria-haspopup="true"
        aria-controls={menuId}
        aria-expanded={isOpen}
      >
        <Box mr={1}>
          <Box alignItems="center">{currentTab && currentTab?.title}</Box>
        </Box>
        <Box alignItems="center" color="primary">
          {isOpen ? <IconArrowUp size={24} /> : <IconArrowDown size={24} />}
        </Box>
      </Dropdown>
      <ItemsList display={isOpen ? 'block' : 'none'} id={menuId}>
        <Box
          as="ul"
          role="menu"
          ref={menuRef}
          aria-labelledby={labelledById}
          flexDirection="column"
          bg="white"
        >
          {preparedItems.map((item) => (
            <Box key={item.title} as="li" role="none" flexDirection="column">
              <MobileItem
                {...item}
                onClick={handleItemPress}
                rootPath={rootPath}
              >
                {item.title}
              </MobileItem>

              {(item?.subItems || []).length > 0 && (
                <Box
                  as="ul"
                  role="menu"
                  aria-label={item.title}
                  pl={4}
                  flexDirection="column"
                >
                  {SubItems(item.subItems, handleItemPress)}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </ItemsList>
    </Box>
  );
};

const MobileMenu = ({
  isLoading = false,
  dynamic = false,
  items = undefined,
  menuId = uuid(),
  labelledById = uuid(),
  ...otherProps
}) => {
  if (isLoading) {
    return (
      <Box alignItems="center" height="40px" px={4} width="100%">
        <Skeleton width="60%" height="24px" />
      </Box>
    );
  }

  return (
    <MobileMenuComponent
      dynamic={dynamic}
      items={items}
      menuId={menuId}
      labelledById={labelledById}
      {...otherProps}
    />
  );
};

MobileMenu.propTypes = {
  dynamic: PropTypes.bool,
  getPageUrl: PropTypes.func,
  items: PropTypes.array,
  isLoading: PropTypes.bool,
  onItemClick: PropTypes.func,
  rootPath: PropTypes.string,
  ariaLabel: PropTypes.string,
  menuId: PropTypes.string,
  labelledById: PropTypes.string,
};

export default MobileMenu;

const Dropdown = styled(Box).attrs({
  bg: 'white',
})`
  border-bottom: 1px solid ${(props) => props.theme.colors.primary};
`;

const ItemsList = styled(Box).attrs(() => ({
  position: 'absolute',
  top: 40,
  right: 0,
  left: 0,
  maxHeight: 240,
  width: '100%',
  bg: 'white',
  overflow: 'auto',
  borderRadius: 2,
  zIndex: zIndices.offcanvas,
}))`
  ${primaryShadow}
`;

const MobileItem = styled(Item)`
  height: 32px;
`;
